// Global study-room voice/video connection.
// ==========================================
// The LiveKit connection used to live INSIDE the Room page, so navigating
// to another tab unmounted it — which disconnected voice and KILLED any
// active screen share, kicking the user out of the room. This provider
// hoists the connection ABOVE the router so it survives navigation: you
// can browse other tabs while staying connected and screen-sharing, then
// return to the room. The floating video tile / raised-hands / reactions
// render globally (position:fixed) so the shared screen stays visible
// everywhere until you explicitly leave.
//
// Resilience: a brief network blip should NOT drop you from voice. LiveKit
// auto-reconnects internally (we surface a "Reconnecting…" toast via
// useConnectionState). If LiveKit gives up entirely (onDisconnected), and
// the user did NOT tap Leave, we attempt a couple of fresh re-joins (new
// token) before finally letting go — so a flaky cell signal recovers.
import React, { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const VoiceRoomConnection = lazy(() => import("../components/VoiceRoomConnection"));

const VoiceRoomContext = createContext(null);
export const useVoiceRoom = () => useContext(VoiceRoomContext) || {};

const RECONNECT_TOAST = "voice-reconnect";
const MAX_REJOIN_ATTEMPTS = 2;

export function VoiceRoomProvider({ children }) {
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [lk, setLk] = useState(null);       // { server_url, token, ... }
  const [controls, setControls] = useState(null); // lifted from VoiceChatUI
  const [reconnectNonce, setReconnectNonce] = useState(0); // bump to re-fetch token

  // Distinguish a user-initiated Leave from an unexpected drop, and cap how
  // many automatic re-joins we attempt before giving up.
  const userLeftRef = useRef(false);
  const rejoinAttemptsRef = useRef(0);
  const firstConnectRef = useRef(true);

  const joinRoom = useCallback((roomId, opts = {}) => {
    if (!roomId) return;
    userLeftRef.current = false;
    rejoinAttemptsRef.current = 0;
    firstConnectRef.current = true;
    setIsOwner(!!opts.isOwner);
    setActiveRoomId((prev) => (prev === roomId ? prev : roomId));
  }, []);

  const leaveRoom = useCallback(() => {
    userLeftRef.current = true;
    toast.dismiss(RECONNECT_TOAST);
    setActiveRoomId(null);
    setLk(null);
    setControls(null);
  }, []);

  // Fetch a fresh LiveKit token whenever the active room changes OR we kick
  // off a re-join (reconnectNonce bump).
  useEffect(() => {
    if (!activeRoomId) { setLk(null); return undefined; }
    let cancelled = false;
    setLk(null);
    (async () => {
      try {
        const { data } = await api.post(`/rooms/${activeRoomId}/voice-token`, {});
        if (!cancelled) setLk(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "Couldn't join voice chat", { id: RECONNECT_TOAST });
          setActiveRoomId(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeRoomId, reconnectNonce]);

  // Presence heartbeat while connected so the room's "N in voice" badge
  // stays accurate for everyone. Backend ages presence out after ~15s.
  useEffect(() => {
    if (!activeRoomId) return undefined;
    const ping = () => { api.post(`/rooms/${activeRoomId}/voice-presence`).catch(() => {}); };
    ping();
    const hb = setInterval(ping, 6000);
    return () => clearInterval(hb);
  }, [activeRoomId]);

  const handleConnected = useCallback(() => {
    rejoinAttemptsRef.current = 0;
    toast.dismiss(RECONNECT_TOAST);
    if (firstConnectRef.current) {
      firstConnectRef.current = false;
      toast.success("You're in — you're muted. Tap 🎙️ Mic to talk.");
    } else {
      toast.success("Reconnected to voice", { id: RECONNECT_TOAST });
    }
  }, []);

  // LiveKit fired onDisconnected — it has exhausted its own internal
  // reconnection. If the user tapped Leave, just clean up. Otherwise try a
  // few fresh re-joins (new token) before giving up, so a flaky connection
  // doesn't silently boot the user out of the study room.
  const handleDisconnected = useCallback(() => {
    if (userLeftRef.current) {
      setActiveRoomId(null);
      setLk(null);
      setControls(null);
      return;
    }
    if (rejoinAttemptsRef.current < MAX_REJOIN_ATTEMPTS) {
      rejoinAttemptsRef.current += 1;
      toast.loading("Reconnecting to voice…", { id: RECONNECT_TOAST });
      setLk(null);
      setTimeout(() => setReconnectNonce((n) => n + 1), 1200);
    } else {
      toast.error("Voice chat disconnected", { id: RECONNECT_TOAST });
      leaveRoom();
    }
  }, [leaveRoom]);

  const handleReconnecting = useCallback(() => {
    toast.loading("Reconnecting to voice…", { id: RECONNECT_TOAST });
  }, []);

  const value = useMemo(() => ({ activeRoomId, controls, joinRoom, leaveRoom }), [activeRoomId, controls, joinRoom, leaveRoom]);

  return (
    <VoiceRoomContext.Provider value={value}>
      {children}
      {activeRoomId && lk && (
        <Suspense fallback={null}>
          <VoiceRoomConnection
            lk={lk}
            isOwner={isOwner}
            onConnected={handleConnected}
            onDisconnected={handleDisconnected}
            onReconnecting={handleReconnecting}
            onLeave={leaveRoom}
            onControls={setControls}
          />
        </Suspense>
      )}
    </VoiceRoomContext.Provider>
  );
}
